<template>
    <drawer-layout>
        <div class="report-container mx-7 mt-4">
            <control-panel
                :breadcrumbs="breadcrumbsItems"
                :title="$t('reports.reports')"
            />

            <template>
                <v-stepper
                    v-model="e2"
                    vertical
                    rounded
                >
                    <v-stepper-step
                        :complete="e2 > 1"
                        step="1"
                        :rules="[() => valid]"
                        error-icon="mdi-alert-circle"
                        editable
                    >
                        {{ $t('reports.data_sources') }}

                        <small
                            v-if="!valid"
                            class="mt-1"
                        >
                            {{ $t('reports.errors.check_form') }}
                        </small>
                    </v-stepper-step>

                    <v-stepper-content step="1">
                        <template>
                            <v-form
                                ref="form"
                                v-model="valid"
                                lazy-validation
                                class=""
                            >
                                <v-col
                                    cols="12"
                                    md="6"
                                    class="pl-0 pt-0"
                                >
                                    <v-row>
                                        <v-col
                                            cols="12"
                                            md="12"
                                            class="py-0"
                                        >
                                            <v-subheader class="form-field-subheader">{{ $t('reports.title') }}:
                                            </v-subheader>
                                            <v-text-field
                                                v-model="form.title"
                                                :rules="rules.reportTitle"
                                                class="input-text input-text--text-field"
                                                outlined
                                                dense
                                                autofocus
                                                required
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>

                                    <v-row>
                                        <v-col
                                            cols="12"
                                            md="12"
                                            class="py-0"
                                        >
                                            <v-subheader class="form-field-subheader">{{ $t('reports.academic_period') }}:
                                            </v-subheader>
                                            <v-select
                                                class="input-text input-text--select body-text"
                                                v-model="form.academic_period"
                                                :items="sortAcademicPeriods"
                                                item-text="name"
                                                item-value="id"
                                                :rules="rules.academicPeriod"
                                                :placeholder="$t('reports.select')"
                                                outlined
                                                dense
                                                @change="getDataSources"
                                            ></v-select>
                                        </v-col>
                                    </v-row>

                                    <v-row>
                                        <v-col
                                            cols="12"
                                            md="12"
                                            class="py-0"
                                        >
                                            <v-subheader class="form-field-subheader">{{ $t('reports.programs') }}:
                                            </v-subheader>
                                            <v-select
                                                class="input-text input-text--select body-text"
                                                v-model="form.programs"
                                                :items="reportPrograms"
                                                item-text="abbreviation"
                                                item-value="id"
                                                :rules="rules.programs"
                                                :placeholder="$t('reports.select')"
                                                outlined
                                                dense
                                                multiple
                                            >
                                                <template v-slot:prepend-item v-if="reportPrograms != undefined">
                                                    <v-list-item
                                                        ripple
                                                        @mousedown.prevent
                                                        @click="programSelectAllToggle"
                                                    >
                                                        <v-list-item-content>
                                                            <v-list-item-title>
                                                                {{$t('app.select_all')}}
                                                            </v-list-item-title>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                    <v-divider class="mt-2"></v-divider>
                                                </template>
                                            </v-select>
                                        </v-col>
                                    </v-row>

                                    <v-row>
                                        <v-container>
                                            <v-subheader class="form-field-subheader">{{ $t('reports.data_sources') }}:
                                            </v-subheader>
                                            <data-source-select
                                                v-for="(reportType, index) in requireReportTypes"
                                                :key="index"
                                                :dataSourceType="reportType"
                                                :configurations="reportConfiguration"
                                                :availableDataSources="dataSources"
                                                :dataSourcesByTypes="dataSourcesByTypes"
                                                :previousSelectedDataSources="form.data_sources"
                                                v-on:onSelectDataSources="setSelectedDataSources"
                                            />
                                        </v-container>
                                    </v-row>

                                    <v-row>
                                        <v-col
                                            cols="12"
                                            md="11"
                                            class="py-0"
                                        >
                                            <v-btn
                                                class="v-btn--primary mr-3"
                                                @click="moveToReportCreationConfirmationStep"
                                                :disabled="!valid"
                                            >
                                                {{ $t('reports.continue') }}
                                            </v-btn>
                                            <v-btn
                                                class="v-btn--secondary"
                                                outlined
                                                @click="cancelReportCreation"
                                                link
                                            >
                                                {{ $t('reports.cancel') }}
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-form>

                            <cancel-report-generation-dialog
                                :showDialog="showDataSourceCancelGenerationDialog"
                                :reportType="reportType"
                                v-on:CancelDialog="showDataSourceCancelGenerationDialog = false"
                                v-on:ConfirmDialog="confirmCancelReportCreation"
                            />
                        </template>
                    </v-stepper-content>

                    <v-stepper-step
                        step="2"
                    >
                        {{ $t('reports.confirmation') }}
                    </v-stepper-step>

                    <v-stepper-content step="2">

                        <report-generation-confirmation
                            :reportTitle="form.title"
                            :academicPeriod="academicPeriodTitle"
                            :dataSources="dataSourceTitles"
                            :programs="reportProgramsAbbreviationsNames"
                        />

                        <div class="pt-5">
                            <v-btn
                                class="v-btn--primary mr-3"
                                @click="saveReport"
                            >
                                {{ $t('reports.request') }}
                            </v-btn>
                            <v-btn
                                class="v-btn--secondary"
                                outlined
                                @click="cancelReportCreation"
                                link
                            >
                                {{ $t('app.cancel') }}
                            </v-btn>
                        </div>

                    </v-stepper-content>
                </v-stepper>
            </template>
        </div>

    </drawer-layout>
</template>

<script>
import {i18n} from "@/i18n";
import toastMixin from "@/Shared/toastMixin";
import _ from "lodash";
import Mixin from "../../../Shared/Reports/mixins/mixin"
import {ReportAnnualDegreeImprovement} from "@/Repositories/Reports/Models/ReportAnnualDegreeImprovement";

export default {

    props: {
        programs: {
            type: Object,
            required: true,
        },
    },

    mixins: [toastMixin,Mixin],

    data() {
        return {
            availablePrograms : [],

            form: ReportAnnualDegreeImprovement.build({report_type: this.reportType.id}),

            dataSourceSelectors : 2,

            rules: {
                programs: [
                    v => v.length > 0 || i18n.t('reports.create_form.required_fields.program'),
                ],
            },

        }
    },

    methods: {

        programSelectAllToggle () {
            this.$nextTick(() => {
                if (this.isAllProgramsLiked) {
                    this.form.programs = []
                } else {
                    this.form.programs = this.reportProgramsIds
                }
            })
        },
        /**
         * Go forward to step 2
         */
        moveToReportCreationConfirmationStep() {
            if(this.$refs.form.validate()) this.e2 = 2;
        },

        /**
         *
         */
        resetForm() {
            this.form = ReportAnnualDegreeImprovement.build();
        },

        /**
         * @returns {Promise<void>}
         */
        async saveReport() {
            let counterErrorFound = 0;

            this.flags.saving = true;

            let formToPost = _.cloneDeep(this.form)

             _.forEach(this.form.programs, async (programId)=>{

                try{
                    delete formToPost['programs']

                    formToPost.program = programId

                    await this.storeReport(formToPost,this.reportTypeName);

                }catch (ex) {
                    counterErrorFound ++;

                    console.error('Report store exception', ex.response);
                }
            })

            if(counterErrorFound === 0){

                this.goBackToReportsIndex();

                this.toast().success(
                    this.$t('reports.create_form.success_store_title', {report: this.form.title}),
                    this.$t('reports.create_form.success_store_message'),
                );

                this.flags.saving = false;

                return;
            }

            if(counterErrorFound === this.form.programs.length)
                this.toast().error(
                    this.$t('reports.create_form.error_store_title'),
                    this.$t('reports.create_form.error_store_message'),
                );
            else{
                this.goBackToReportsIndex();

                this.toast().warning(
                    this.$t('reports.create_form.error_store_multiple_title'),
                    this.$t('reports.create_form.error_store_multiple_message'),
                );
            }

            this.flags.saving = false;

        },

    },

    computed: {

        isAllProgramsLiked () {
            return this.form.programs.length === this.reportPrograms.length
        },
        /**
         *
         * @returns {*}
         */
        reportPrograms() {
            return _.get(this.programs, '_embedded.programs');
        },

        reportProgramsIds(){
            return _.map(this.reportPrograms,
                (value) => value.id
            );
        },

        reportProgramsAbbreviations(){

            let programs =_.filter(this.reportPrograms,(value)=>{
                return this.form.programs.includes(value.id)
            })

            return programs === undefined? []: programs

        },

        reportProgramsAbbreviationsNames(){

            return _.map(this.reportProgramsAbbreviations, (value) => value.abbreviation);

        }

    }
}
</script>

<style scoped>
    .form-field-subheader {
        padding: 0;
    }
</style>
